import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { DISPATCH_EVENT_KEYS } from 'src/consumer/constants'
import { checkOutRoom } from 'src/clinic/components/CheckIn/services'
import {
  getOutsideEmrZoneGraceMs,
  isEmrCheckInZonePath,
  isLegacyPractitionerSessionRedirectPath,
} from 'src/clinic/components/CheckIn/checkInZoneRoutes'
import {
  broadcastCheckInUpdate,
  broadcastCheckInRemoval,
  updateCheckInWithValidation,
  initCheckInBroadcastChannel,
  cleanupCheckInBroadcastChannel,
  refreshCurrentCheckInStatus,
  CHECK_IN_REFRESH_STATUS,
} from 'src/clinic/components/CheckIn/checkInSyncService'

const HEARTBEAT_INTERVAL_MS = 90 * 1000
const INITIAL_HEARTBEAT_DELAY_MS = 5000
const EMPTY_CHECKIN_RECHECK_DELAY_MS = 3000
const SHARED_HEARTBEAT_THROTTLE_MS = 45 * 1000
const ZONE_PRESENCE_INTERVAL_MS = 30 * 1000
const CHECKOUT_LOCK_MS = 30 * 1000
const LAST_HEARTBEAT_STORAGE_KEY = 'checkedInLastHeartbeatAt'
const LAST_ZONE_PRESENCE_STORAGE_KEY = 'checkedInLastEmrZonePresenceAt'
const LEFT_EMR_ZONE_AT_STORAGE_KEY = 'checkedInLeftEmrZoneAt'
const OUTSIDE_ZONE_CHECKOUT_LOCK_STORAGE_KEY = 'checkedInOutsideZoneCheckoutInProgressAt'

function getCurrentOutsideZoneGraceMs() {
  return getOutsideEmrZoneGraceMs(localStorage.getItem('authorities'))
}

function isWaitingForMentorSessionRedirect() {
  return isLegacyPractitionerSessionRedirectPath(
    window.location.pathname,
    localStorage.getItem('authorities'),
  )
}

/**
 * The same 'checkedInDetails' key also holds a clinic assistant's own reception check-in (a
 * CAUsageLog: carries clinicAssistantId / clinicAssistantUserId). This manager only looks after
 * practitioner chamber check-ins, so it must never refresh or check out a clinic-assistant record:
 * doing so hit the practitioner-only /teacher/emr/usage/log/* endpoints (Access Denied) from the
 * clinic assistant portal.
 */
export function getStoredPractitionerCheckIn() {
  const raw = localStorage.getItem('checkedInDetails')
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (parsed?.clinicAssistantId || parsed?.clinicAssistantUserId) return null
    return raw
  } catch {
    return null
  }
}

export function isClinicAssistantPortalPath(pathname = '') {
  return /^\/ca-home(\/|$)/.test(pathname || '')
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const HeartbeatManager = () => {
  const location = useLocation()
  const heartbeatInProgressRef = useRef(false)
  const isInEmrZoneRef = useRef(false)
  const outsideZoneTimerRef = useRef(null)
  const pathname = location?.pathname || ''
  const isInEmrZone = useMemo(() => isEmrCheckInZonePath(pathname), [pathname])

  const clearOutsideZoneTimer = useCallback(() => {
    if (outsideZoneTimerRef.current) {
      clearTimeout(outsideZoneTimerRef.current)
      outsideZoneTimerRef.current = null
    }
  }, [])

  const clearLocalCheckIn = useCallback(() => {
    localStorage.removeItem('checkedInDetails')
    localStorage.removeItem('peekedInRoomDetails')
    localStorage.removeItem('roomDataEventTypes')
    localStorage.removeItem(LAST_HEARTBEAT_STORAGE_KEY)
    localStorage.removeItem(LAST_ZONE_PRESENCE_STORAGE_KEY)
    localStorage.removeItem(LEFT_EMR_ZONE_AT_STORAGE_KEY)
    localStorage.removeItem(OUTSIDE_ZONE_CHECKOUT_LOCK_STORAGE_KEY)
    clearOutsideZoneTimer()
    broadcastCheckInRemoval()
    window.dispatchEvent(new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedOut, { detail: {} }))
  }, [clearOutsideZoneTimer])

  const markLocalCheckInValidationError = useCallback((reason) => {
    const checkedInDetails = getStoredPractitionerCheckIn()
    if (!checkedInDetails) return

    try {
      const parsed = JSON.parse(checkedInDetails)
      localStorage.setItem(
        'checkedInDetails',
        JSON.stringify({
          ...parsed,
          lastValidated: new Date().toISOString(),
          validationError: true,
          validationErrorReason: reason || 'heartbeat-refresh-unknown',
        }),
      )
    } catch (error) {
      console.error('[HeartbeatManager] Could not mark check-in validation error:', error)
    }
  }, [])

  const shouldSkipForSharedHeartbeat = useCallback((force = false) => {
    if (force) return false
    const lastHeartbeatAt = Number(localStorage.getItem(LAST_HEARTBEAT_STORAGE_KEY) || 0)
    return lastHeartbeatAt > 0 && Date.now() - lastHeartbeatAt < SHARED_HEARTBEAT_THROTTLE_MS
  }, [])

  const markZonePresence = useCallback(() => {
    if (!getStoredPractitionerCheckIn()) {
      return
    }
    localStorage.setItem(LAST_ZONE_PRESENCE_STORAGE_KEY, String(Date.now()))
    localStorage.removeItem(LEFT_EMR_ZONE_AT_STORAGE_KEY)
  }, [])

  const hasRecentEmrZonePresence = useCallback(() => {
    const lastZonePresenceAt = Number(localStorage.getItem(LAST_ZONE_PRESENCE_STORAGE_KEY) || 0)
    return (
      lastZonePresenceAt > 0 &&
      Date.now() - lastZonePresenceAt < getCurrentOutsideZoneGraceMs()
    )
  }, [])

  const acquireOutsideZoneCheckoutLock = useCallback(() => {
    const lockAt = Number(localStorage.getItem(OUTSIDE_ZONE_CHECKOUT_LOCK_STORAGE_KEY) || 0)
    if (lockAt > 0 && Date.now() - lockAt < CHECKOUT_LOCK_MS) {
      return false
    }
    localStorage.setItem(OUTSIDE_ZONE_CHECKOUT_LOCK_STORAGE_KEY, String(Date.now()))
    return true
  }, [])

  const releaseOutsideZoneCheckoutLock = useCallback(() => {
    localStorage.removeItem(OUTSIDE_ZONE_CHECKOUT_LOCK_STORAGE_KEY)
  }, [])

  const performOutsideZoneCheckout = useCallback(async () => {
    if (
      isInEmrZoneRef.current ||
      isWaitingForMentorSessionRedirect() ||
      isClinicAssistantPortalPath(window.location.pathname) ||
      !getStoredPractitionerCheckIn()
    ) {
      return
    }

    if (hasRecentEmrZonePresence()) {
      return
    }

    if (!acquireOutsideZoneCheckoutLock()) {
      return
    }

    try {
      const checkedInDetails = JSON.parse(getStoredPractitionerCheckIn())
      const response = await checkOutRoom(checkedInDetails, null, {
        refType: 'outside-emr-zone-timeout',
      })
      if (response?.status === 200) {
        toast.info('You left the EMR workspace, so your chamber check-in has ended.', {
          toastId: 'chamber-check-in-left-emr-zone',
        })
      } else {
        markLocalCheckInValidationError('outside-zone-checkout-failed')
      }
    } catch (error) {
      console.error('[HeartbeatManager] Could not checkout after leaving EMR zone:', error)
      markLocalCheckInValidationError('outside-zone-checkout-threw')
    } finally {
      releaseOutsideZoneCheckoutLock()
    }
  }, [
    acquireOutsideZoneCheckoutLock,
    hasRecentEmrZonePresence,
    markLocalCheckInValidationError,
    releaseOutsideZoneCheckoutLock,
  ])

  const scheduleOutsideZoneGraceCheck = useCallback(() => {
    clearOutsideZoneTimer()

    if (
      isInEmrZoneRef.current ||
      isWaitingForMentorSessionRedirect() ||
      isClinicAssistantPortalPath(window.location.pathname) ||
      !getStoredPractitionerCheckIn()
    ) {
      return
    }

    let leftAt = Number(localStorage.getItem(LEFT_EMR_ZONE_AT_STORAGE_KEY) || 0)
    const outsideZoneGraceMs = getCurrentOutsideZoneGraceMs()
    if (!leftAt) {
      leftAt = Date.now()
      localStorage.setItem(LEFT_EMR_ZONE_AT_STORAGE_KEY, String(leftAt))
      const graceMinutes = Math.round(outsideZoneGraceMs / (60 * 1000))
      toast.info(
        `You left the EMR workspace. Return within ${graceMinutes} minutes to keep this chamber check-in active.`,
        {
          toastId: 'chamber-check-in-left-emr-zone-warning',
        },
      )
    }

    const elapsed = Date.now() - leftAt
    const remaining = Math.max(0, outsideZoneGraceMs - elapsed)

    outsideZoneTimerRef.current = setTimeout(() => {
      performOutsideZoneCheckout()
    }, remaining)
  }, [clearOutsideZoneTimer, performOutsideZoneCheckout])

  const sendHeartbeat = useCallback(async ({ force = false } = {}) => {
    if (!isInEmrZoneRef.current) {
      return
    }

    // Prevent concurrent heartbeats
    if (heartbeatInProgressRef.current) {
      return
    }
    
    heartbeatInProgressRef.current = true
    
    try {
      markZonePresence()
      const checkedInDetails = getStoredPractitionerCheckIn()
      
      if (!checkedInDetails) {
        // If we didn't find check-in, wait a bit
        // in case validation is still in progress (validation runs async and may take time with retries)
        await sleep(EMPTY_CHECKIN_RECHECK_DELAY_MS)
        if (!getStoredPractitionerCheckIn()) {
          return
        }
      }

      if (shouldSkipForSharedHeartbeat(force)) {
        return
      }
      localStorage.setItem(LAST_HEARTBEAT_STORAGE_KEY, String(Date.now()))

      const refreshResult = await refreshCurrentCheckInStatus()

      if (refreshResult?.status === CHECK_IN_REFRESH_STATUS.active) {
        // Success - update localStorage with fresh data and validation timestamp
        const emrUsageLog = refreshResult.emrUsageLog
        const chamberRoom = refreshResult.chamberRoom
        
        if (emrUsageLog) {
          // Update with validation timestamp
          updateCheckInWithValidation(emrUsageLog, chamberRoom)
          
          // Update chamber room if provided
          if (chamberRoom) {
            localStorage.setItem('peekedInRoomDetails', JSON.stringify(chamberRoom))
            // Broadcast update with chamber room
            broadcastCheckInUpdate(emrUsageLog, chamberRoom)
          } else {
            // Broadcast update without chamber room
            broadcastCheckInUpdate(emrUsageLog)
          }
          
        }
        return
      }

      if (refreshResult?.status === CHECK_IN_REFRESH_STATUS.unknown) {
        console.warn('[HeartbeatManager] Check-in refresh status unknown, keeping local state')
        markLocalCheckInValidationError(refreshResult?.reason)
        return
      }

      if (refreshResult?.status === CHECK_IN_REFRESH_STATUS.inactive) {
        console.warn('[HeartbeatManager] Server rejected check-in, removing from localStorage')
        clearLocalCheckIn()
        // Code 4347 = a different practitioner checked in and took this chamber over. Show that
        // specific reason (the server message names them) so the incumbent understands why their
        // session ended, rather than a generic "your check-in ended".
        const wasTakenOver = refreshResult?.response?.data?.code === 4347
        const takeoverMessage = refreshResult?.response?.data?.message
        toast.info(
          wasTakenOver && takeoverMessage
            ? takeoverMessage
            : 'Your chamber check-in has ended. You can check in again from My Clinic Associations if needed.',
          {
            toastId: 'chamber-check-in-ended',
          },
        )
      }
    } catch (err) {
      console.error('[HeartbeatManager] Unexpected error during heartbeat:', err)
      markLocalCheckInValidationError('heartbeat-threw')
    } finally {
      heartbeatInProgressRef.current = false
    }
  }, [
    clearLocalCheckIn,
    markLocalCheckInValidationError,
    markZonePresence,
    shouldSkipForSharedHeartbeat,
  ])

  useEffect(() => {
    // Initialize BroadcastChannel for multi-tab sync
    initCheckInBroadcastChannel()
    
    // Set interval to send heartbeat every 90 seconds
    const intervalId = setInterval(() => {
      sendHeartbeat()
    }, HEARTBEAT_INTERVAL_MS)
    
    // Send initial heartbeat after a delay (longer to allow validation to complete)
    // Validation starts after 1 second, so we wait 5 seconds to ensure it completes
    const initialTimeout = setTimeout(() => {
      sendHeartbeat({ force: true })
    }, INITIAL_HEARTBEAT_DELAY_MS)

    const handlePageBecameActive = () => {
      if (isInEmrZoneRef.current) {
        sendHeartbeat({ force: true })
      } else {
        scheduleOutsideZoneGraceCheck()
      }
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handlePageBecameActive()
      }
    }
    const handleCheckInChanged = () => {
      setTimeout(() => {
        if (isInEmrZoneRef.current) {
          sendHeartbeat({ force: true })
        } else {
          scheduleOutsideZoneGraceCheck()
        }
      }, 1000)
    }

    window.addEventListener('focus', handlePageBecameActive)
    window.addEventListener('online', handlePageBecameActive)
    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, handleCheckInChanged)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Clean up interval and timeout on component unmount
    return () => {
      clearInterval(intervalId)
      clearTimeout(initialTimeout)
      window.removeEventListener('focus', handlePageBecameActive)
      window.removeEventListener('online', handlePageBecameActive)
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, handleCheckInChanged)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      cleanupCheckInBroadcastChannel()
    }
  }, [scheduleOutsideZoneGraceCheck, sendHeartbeat])

  useEffect(() => {
    isInEmrZoneRef.current = isInEmrZone

    if (isInEmrZone) {
      clearOutsideZoneTimer()
      markZonePresence()
      setTimeout(() => sendHeartbeat({ force: true }), 1000)
      return
    }

    scheduleOutsideZoneGraceCheck()
  }, [clearOutsideZoneTimer, isInEmrZone, markZonePresence, scheduleOutsideZoneGraceCheck, sendHeartbeat])

  useEffect(() => {
    if (!isInEmrZone) {
      return undefined
    }

    markZonePresence()
    const zonePresenceInterval = setInterval(markZonePresence, ZONE_PRESENCE_INTERVAL_MS)
    return () => clearInterval(zonePresenceInterval)
  }, [isInEmrZone, markZonePresence])

  useEffect(() => clearOutsideZoneTimer, [clearOutsideZoneTimer])

  return null // This component does not render anything
}

export default HeartbeatManager
